/**
 * Assumptions & Risks — plan-cycle scoped lists (EmbeddedListTable / ListTable).
 *
 * Risks come from `fetchPlanRisks`: the plan's narrative (`por_risks`, where
 * people write them) and its version (`risks`, where the API writes them). This
 * page only reads. Adding a risk is RisksPanel's, in the plan narrative.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { EntityListShell } from '../../components/collection/EntityListShell';
import { EmbeddedListTable } from '../../components/collection/EmbeddedListTable';
import type { ListTableColumnDef } from '../../components/collection/ListTable';
import { getSupabaseClient } from '../../lib/supabaseClient';
import type { Assumption } from '../../types/planning';
import { fetchPlanRisks, type PlanRiskRow } from '../../lib/planRisks';
import { SeverityPill } from '../../components/plan-of-record/porUi';
import { usePlanNarrativeEnabled } from '../../hooks/usePlanNarrativeEnabled';
import {
  PlanningCycleScope,
  type PlanningCycleContext,
} from '../../components/planning/PlanningCycleScope';

function AssumptionsRisksContent({ ctx }: { ctx: PlanningCycleContext }) {
  const [assumptions, setAssumptions] = useState<Assumption[]>([]);
  const [risks, setRisks] = useState<PlanRiskRow[]>([]);
  const [riskError, setRiskError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const narrativeEnabled = usePlanNarrativeEnabled();

  useEffect(() => {
    if (!ctx.planVersionId) return;
    let cancelled = false;
    void getSupabaseClient()
      .from('assumptions')
      .select('*')
      .eq('plan_version_id', ctx.planVersionId)
      .then((res) => {
        if (!cancelled) setAssumptions((res.data ?? []) as Assumption[]);
      });
    return () => {
      cancelled = true;
    };
  }, [ctx.planVersionId]);

  useEffect(() => {
    let cancelled = false;
    void fetchPlanRisks(ctx.cycleId, ctx.planVersionId || null).then(({ rows, error }) => {
      if (cancelled) return;
      setRisks(rows);
      setRiskError(error);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.cycleId, ctx.planVersionId]);

  const filteredAssumptions = searchQuery
    ? assumptions.filter((a) => a.statement.toLowerCase().includes(searchQuery.toLowerCase()))
    : assumptions;
  const filteredRisks = searchQuery
    ? risks.filter((r) => r.description.toLowerCase().includes(searchQuery.toLowerCase()))
    : risks;

  const assumptionColumns = useMemo((): ListTableColumnDef<Assumption>[] => [
    {
      key: 'statement',
      label: 'Assumption',
      defaultWidth: 320,
      render: (a) => (
        <span className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] block truncate min-w-0 w-full">
          {a.statement}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      defaultWidth: 120,
      render: (a) => {
        const tone =
          a.status === 'broken' || a.status === 'invalidated'
            ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]'
            : a.status === 'confirmed' || a.status === 'valid'
              ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]'
              : 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
        return (
          <span className={`text-xs px-2 py-0.5 rounded inline-block ${tone}`}>
            {a.status}
            {a.source_key?.startsWith('tp:') ? ' · engine' : ''}
          </span>
        );
      },
    },
  ], []);

  const riskColumns = useMemo((): ListTableColumnDef<PlanRiskRow>[] => [
    {
      key: 'description',
      label: 'Risk',
      defaultWidth: 320,
      render: (r) => (
        <span className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] block truncate min-w-0 w-full">
          {r.description}
        </span>
      ),
    },
    {
      key: 'severity',
      label: 'Severity',
      defaultWidth: 100,
      render: (r) => (r.severity ? <SeverityPill severity={r.severity} /> : <span className="text-sm">—</span>),
    },
    {
      key: 'mitigation',
      label: 'Mitigation',
      defaultWidth: 240,
      render: (r) => (
        <span className="text-sm text-[var(--text-sec)] block truncate min-w-0 w-full">{r.mitigation ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      defaultWidth: 100,
      render: (r) => <span className="text-sm">{r.status ?? '—'}</span>,
    },
    {
      key: 'impact',
      label: 'Impact',
      align: 'right',
      defaultWidth: 80,
      render: (r) => <span className="tabular-nums text-sm">{r.impact ?? '—'}</span>,
    },
    {
      key: 'likelihood',
      label: 'Likelihood',
      align: 'right',
      defaultWidth: 96,
      render: (r) => <span className="tabular-nums text-sm">{r.likelihood ?? '—'}</span>,
    },
  ], []);

  return (
    <EntityListShell
      className="h-full"
      hideToolbar
      icon={<AlertTriangle className="h-4 w-4 text-primary-600" />}
      iconBgClassName="bg-primary-50"
      title="Assumptions & Risks"
      subtitle={`${assumptions.length} assumption${assumptions.length !== 1 ? 's' : ''} · ${risks.length} risk${risks.length !== 1 ? 's' : ''}`}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search assumptions and risks…"
      contextBar={{
        label: `Plan version ${ctx.planVersionId.slice(0, 8)}…`,
      }}
    >
      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-8">
        <EmbeddedListTable
          title="Assumptions"
          items={filteredAssumptions}
          getId={(a) => a.id}
          columns={assumptionColumns}
          emptyTitle="No assumptions"
          emptyDescription="Assumptions for this plan version will appear here."
        />
        <EmbeddedListTable
          title="Risks"
          items={filteredRisks}
          getId={(r) => r.id}
          columns={riskColumns}
          emptyTitle="No risks"
          emptyDescription="Risks written in this plan's narrative will appear here."
        />
        {riskError && (
          <p role="alert" className="text-sm text-[var(--coral)]">
            Could not read every risk on this plan: {riskError}
          </p>
        )}
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {narrativeEnabled && (
            <Link to={`/planning/plans/${ctx.cycleId}/narrative`} className="text-sm text-primary-600 hover:underline">
              Add or change risks in the plan narrative →
            </Link>
          )}
          <Link to={`/planning/plans/${ctx.cycleId}`} className="text-sm text-primary-600 hover:underline">
            View plan →
          </Link>
        </div>
      </div>
    </EntityListShell>
  );
}

export default function PlanningAssumptionsRisks() {
  const { cycleId: cycleIdFromParams } = useParams<{ cycleId?: string }>();
  const [searchParams] = useSearchParams();
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const resolvedCycleId = cycleIdFromParams ?? searchParams.get('cycleId');

  return (
    <div className="h-full min-h-[520px]">
      <PlanningCycleScope
        cycleIdParam={resolvedCycleId}
        selectedCycleId={selectedCycleId}
        onSelectCycle={(id) => setSelectedCycleId(id)}
      >
        {(ctx) => <AssumptionsRisksContent ctx={ctx} />}
      </PlanningCycleScope>
    </div>
  );
}
